import {
  Group,
  List,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconCalculator,
  IconCalendarEvent,
  IconFileText,
  IconHandStop,
  IconHeartHandshake,
  IconMessage,
  IconSchool,
  IconShield,
  IconToolsKitchen2,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  MARKETING_DOMAIN_ANCHORS,
  MARKETING_FEATURE_DOMAIN_IDS,
} from '#marketing/lib/marketing-capabilities.js';

/** @type {Record<string, import('@tabler/icons-react').Icon>} */
const DOMAIN_ICONS = {
  hotLunch: IconToolsKitchen2,
  calendar: IconCalendarEvent,
  volunteers: IconHandStop,
  communications: IconMessage,
  meetings: IconUsersGroup,
  documents: IconFileText,
  schoolYear: IconSchool,
  fundraising: IconHeartHandshake,
  budget: IconCalculator,
  trustPlatform: IconShield,
};

/**
 * Anchorable deep-dive section for one feature domain on /features.
 *
 * @param {object} props
 * @param {string} props.domainId
 */
export function FeatureDomainSection({ domainId }) {
  const { t } = useTranslation('common');
  const anchor = MARKETING_DOMAIN_ANCHORS[domainId];
  const Icon = DOMAIN_ICONS[domainId] ?? IconCalendarEvent;
  const bullets = t(`marketing.featureDomains.${domainId}.bullets`, {
    returnObjects: true,
  });

  return (
    <Paper
      id={anchor}
      withBorder
      radius="md"
      p="xl"
      bg="white"
      component="section"
      style={{ scrollMarginTop: '5rem' }}
    >
      <Group wrap="nowrap" align="flex-start" gap="md" mb="md">
        <ThemeIcon color="pachubColourScheme" size={48} radius="md" variant="light">
          <Icon size={26} stroke={1.5} />
        </ThemeIcon>
        <Stack gap="xs" style={{ flex: 1 }}>
          <Title order={3}>{t(`marketing.featureDomains.${domainId}.title`)}</Title>
          <Text c="dimmed">{t(`marketing.featureDomains.${domainId}.pitch`)}</Text>
        </Stack>
      </Group>
      {Array.isArray(bullets) && (
        <List spacing="sm" size="sm" withPadding>
          {bullets.map((bullet) => (
            <List.Item key={bullet}>{bullet}</List.Item>
          ))}
        </List>
      )}
    </Paper>
  );
}

/**
 * All feature domain sections for the features page.
 */
export function FeatureDomainSections() {
  return (
    <Stack gap="lg">
      {MARKETING_FEATURE_DOMAIN_IDS.map((domainId) => (
        <FeatureDomainSection key={domainId} domainId={domainId} />
      ))}
    </Stack>
  );
}
